import type { ReactNode } from "react";
import { z } from "zod";
import { useForm } from "@mantine/form";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useTRPC } from "../../trpc";
import { useCallback, useMemo } from "react";
import { RelativeLink } from "../../components/Link";
import { useSearch } from "@tanstack/react-router";
import { IconAt, IconExclamationCircle, IconInbox, IconLock } from "@tabler/icons-react";
import { FormattedMessage, useIntl } from "react-intl";
import { emailSchema, passwordSchemaLength } from "../../utils/schema";
import { captureEvent } from "../../utils/posthog";
import { useSetUser } from "../../hooks/user";
import { useMutation } from "@tanstack/react-query";
import { Event } from "../../constants/events";
import { useNavigateUserAuth } from "./hooks/navigate";
import { GithubButton, GoogleButton } from "./components/SocialLogins";
import {
  Alert,
  Button,
  Divider,
  Group,
  PasswordInput,
  Stack,
  TextInput,
  Title,
  Anchor,
} from "@mantine/core";

const initialValues = {
  email: "",
  password: "",
  persistent: true,
};

export function UserSignIn() {
  const intl = useIntl();
  const trpc = useTRPC();
  const navigate = useNavigateUserAuth();
  const setUser = useSetUser();

  const { shouldVerifyEmail = false } = useSearch({
    strict: false,
  });

  const { mutate, error, isPending } = useMutation(trpc.signIn.mutationOptions({
    onSuccess: (user) => {
      setUser(user);
      navigate(user);
    },
  }));

  const validate = useMemo(() => zod4Resolver(
    z.object({
      email: emailSchema(intl),
      persistent: z.boolean(),
      password: passwordSchemaLength(intl),
    }),
  ), [intl]);

  const { onSubmit, getInputProps } = useForm({
    validateInputOnBlur: true,
    initialValues,
    validate,
  });

  const handleSubmit = useCallback((form: typeof initialValues) => {
    captureEvent(Event.AuthSignedIn, {
      persistent: form.persistent,
    });
    mutate({
      username: form.email,
      password: form.password,
      persistent: form.persistent,
    });
  }, [mutate]);

  let errorMessage: ReactNode = null;
  if (error) {
    if (error.message.includes("User or password are incorrect")) {
      errorMessage = (
        <FormattedMessage
          id="error.invalidEmailOrPassword"
          defaultMessage="Email or password are incorrect"
        />
      );
    }
    else if (error.message.includes("Too many login attempts for user")) {
      errorMessage = (
        <FormattedMessage
          id="error.tooManyLoginAttempts"
          defaultMessage="Too many login attempts"
        />
      );
    }
    else if (error.message.includes("User should use social login")) {
      errorMessage = (
        <FormattedMessage
          id="error.socialLoginOnly"
          defaultMessage="User should use social login"
        />
      );
    }
    else {
      errorMessage = error.message;
    }
  }

  const shouldVerifyEmailMessage = (
    <FormattedMessage
      id="alert.shouldVerifyEmail"
      defaultMessage="Check your mailbox and spam folder. You need to verify your email to continue."
    />
  );

  return (
    <Stack>
      <Title size="h2">
        <FormattedMessage
          id="title.signIn"
          defaultMessage="Sign in"
        />
      </Title>
      {error && (
        <Alert color="red" icon={<IconExclamationCircle />}>
          {errorMessage}
        </Alert>
      )}
      {shouldVerifyEmail && (
        <Alert variant="light" color="yellow" icon={<IconInbox />}>
          {shouldVerifyEmailMessage}
        </Alert>
      )}
      <Stack gap={12}>
        <Anchor href="/api/login/google" w="100%" underline="never" onClick={() => { captureEvent(Event.AuthLoginWithGoogle); }}>
          <GoogleButton variant="default" fullWidth>
            <FormattedMessage
              id="button.continueWithGoogle"
              defaultMessage="Continue with Google"
            />
          </GoogleButton>
        </Anchor>
        <Anchor href="/api/login/github" w="100%" underline="never" onClick={() => { captureEvent(Event.AuthLoginWithGithub); }}>
          <GithubButton variant="default" fullWidth>
            <FormattedMessage
              id="button.continueWithGithub"
              defaultMessage="Continue with Github"
            />
          </GithubButton>
        </Anchor>
      </Stack>
      <Divider label={(
        <FormattedMessage
          id="divider.or"
          defaultMessage="OR"
        />
      )}
      />
      <form onSubmit={onSubmit(handleSubmit)}>
        <Stack gap={16}>
          <TextInput
            leftSection={<IconAt size={16} />}
            label={(
              <FormattedMessage
                id="label.email"
                defaultMessage="Email"
              />
            )}
            type="email"
            autoComplete="email"
            required
            {...getInputProps("email")}
          />
          <PasswordInput
            styles={{
              label: {
                width: "100%",
              },
            }}
            leftSection={<IconLock size={16} />}
            label={(
              <Group justify="space-between" w="100%">
                <FormattedMessage
                  id="label.password"
                  defaultMessage="Password"
                />
                <RelativeLink underline="always" href="../reset-password" size="sm" onClick={() => { captureEvent(Event.AuthNavigateToResetPassword); }}>
                  <FormattedMessage
                    id="link.forgotPassword"
                    defaultMessage="Forgot password?"
                  />
                </RelativeLink>
              </Group>
            )}
            type="password"
            autoComplete="current-password"
            {...getInputProps("password")}
          />
          <Button type="submit" fullWidth loading={isPending} variant="primary">
            <FormattedMessage
              id="button.continue"
              defaultMessage="Continue"
            />
          </Button>
        </Stack>
      </form>
      <Group gap={4}>
        <FormattedMessage
          id="text.noAccount"
          defaultMessage="Don't have an account?"
        />
        <RelativeLink
          href="../register"
          size="md"
          underline="always"
          onClick={() => { captureEvent(Event.AuthNavigateToRegister); }}
        >
          <FormattedMessage
            id="link.createAccount"
            defaultMessage="Create an account"
          />
        </RelativeLink>
      </Group>
    </Stack>
  );
}
